import React from "react";
import { Container, Toolbar, TableHead, TableBody, TableCell, TableRow, Table, TableSortLabel, Switch, Grid, SvgIcon } from "@material-ui/core";
import moment from "moment";
import Services from "../../services/Services";
import WithLoadingSpinners from "../hoc/WithLoadingSpinner";
import styled from "styled-components";
import snackbarService from "../../services/ui-messaging/SnackbarService";
import { H1 } from "../Headers";
import { connect } from "react-redux";
import { IApplicationState } from "../../store";
import { selectAuth } from "../../store/auth/selectors";
import BlockQuote from "../BlockQuote";
import CompleteSignUpButton from "./CompleteSignUpButton";
import IAdminPanelServiceCall from "../../models/IAdminPanelServiceCall";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { getArea, formatDescription } from "../../functions/serviceCallFunctions";

const SettingsGrid = styled.div`
  margin-left: 2em;
`;

const TitleToolbar = styled(Toolbar)`
  flex-direction: column;
`;

const TitleHeader = styled(H1)`
  margin-bottom: 1em;
  line-height: 40px;
`;

const SubTitle = styled(BlockQuote)`
  margin-top: 0;
`;

const HiddenTableRow = styled(TableRow)<{
  open: boolean;
}>`
  ${props =>
    !props.open &&
    `
    display: none;
  `}

  ${props =>
    props.open &&
    `
    display: table-row;
  `}
`;

const StyledTableCell = styled(TableCell)`
  background-color: #d9d9d9;
  vertical-align: text-top;
  padding: 8px;
`;

interface HeaderCell {
  id: keyof IAdminPanelServiceCall;
  label: string;
  numeric: boolean;
}

const headerCellData: HeaderCell[] = [
  { id: "id", label: "Ticket #", numeric: true },
  { id: "internalStatus", label: "Status", numeric: false },
  { id: "companyName", label: "Company", numeric: false },
  { id: "firstName", label: "Requested By", numeric: false },
  { id: "dateSubmitted", label: "Date Requested", numeric: false },
];

interface ITableHeadProps {
  order: Order;
  orderBy: string;
  rowCount: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof IAdminPanelServiceCall) => void;
}

function FormatTableHead(props: ITableHeadProps) {
  const createSortHandler = (property: keyof IAdminPanelServiceCall) => (event: React.MouseEvent<unknown>) => {
    props.onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell />
        {headerCellData.map(hc => (
          <TableCell key={hc.id} sortDirection={props.orderBy === hc.id ? props.order : false}>
            <TableSortLabel active={props.orderBy === hc.id} direction={props.order} onClick={createSortHandler(hc.id)}>
              {hc.label}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell />
        <TableCell />
      </TableRow>
    </TableHead>
  );
}

const FormatTableToolbar = () => {
  return (
    <TitleToolbar>
      <TitleHeader>Signup Requests</TitleHeader>
      <SubTitle>
        Requests must be in a <em>hold</em> status in order to complete them.
      </SubTitle>
    </TitleToolbar>
  );
};

function GetNewImage(scDate: string) {
  var newDate = new Date(scDate);
  var curDate = new Date();

  if (newDate.getFullYear() === curDate.getFullYear() && newDate.getMonth() === curDate.getMonth() && newDate.getDate() === curDate.getDate()) {
    return (
      <SvgIcon viewBox="0 0 35 35" component="svg" titleAccess="New today!">
        <path
          id="path3723"
          d="m 19.388336,35.936694 c -0.366894,-0.1182 -0.693965,-0.4162 -2.011565,-1.8329 -0.550469,-0.5919 -0.917531,-0.9588 -0.94761,-0.9473 -0.02701,0.01 -0.318247,0.2128 -0.647202,0.4499 -1.816251,1.3088 -2.189923,1.5548 -2.503144,1.6478 -0.238129,0.071 -0.298916,0.069 -0.582947,-0.019 -0.473665,-0.1463 -0.680206,-0.4889 -1.283582,-2.1291 -0.100456,-0.2731 -0.282288,-0.7507 -0.404068,-1.0614 l -0.221421,-0.5648 -0.24337,0.024 c -0.133857,0.013 -0.50776,0.082 -0.8309013,0.153 -2.1304113,0.4695 -2.3872296,0.5158 -2.696614,0.4861 -0.3868138,-0.037 -0.7612978,-0.3446 -0.852748,-0.7002 -0.038788,-0.1508 -0.046634,-0.5771 -0.036992,-2.0102 l 0.012233,-1.8187 -1.315807,-0.2129 c -1.5420226,-0.2494 -2.1835367,-0.3724 -2.3255948,-0.4458 -0.1369687,-0.071 -0.4235525,-0.4017 -0.5200426,-0.6004 -0.064119,-0.132 -0.073945,-0.2019 -0.056744,-0.4039 0.029003,-0.3404 0.1599868,-0.6864 0.8143678,-2.1507 0.3138321,-0.7023 0.5798104,-1.3135 0.5910605,-1.3584 0.018287,-0.073 -0.064609,-0.1336 -0.780485,-0.5719 -1.0831717,-0.6632 -1.7796839,-1.1089 -2.0751825,-1.328 C 0.1324686,20.291794 0,20.034994 0,19.630794 c 0,-0.5094 0.1164622,-0.6531 1.7992953,-2.2203 0.4746094,-0.442 0.876328,-0.8262 0.8927062,-0.8537 0.016381,-0.028 0.019653,-0.082 0.00728,-0.121 -0.034654,-0.1092 -0.3384779,-0.5621 -0.9083132,-1.354 -1.1578664,-1.6093 -1.2694209,-1.8497 -1.1088778,-2.3903 0.1316699,-0.4434 0.4621612,-0.635 2.2738991,-1.3183 1.4506385,-0.5471 1.486405,-0.5622 1.4865234,-0.6301 2.754e-4,-0.1326 -0.092004,-0.6042 -0.3123862,-1.5973002 -0.3322456,-1.4972 -0.4004738,-1.9191 -0.3490892,-2.1587 0.1090511,-0.5085 0.5238733,-0.8408 1.1172913,-0.895 0.1232895,-0.011 0.9161457,0.01 1.761904,0.041 0.9734652,0.039 1.5505683,0.049 1.5726997,0.027 0.0499,-0.05 0.080508,-0.2026 0.324851,-1.6212 0.2692881,-1.5634 0.3476401,-1.9226 0.4632101,-2.1238 0.182864,-0.3182 0.602024,-0.5125 1.0212693,-0.4734 0.305587,0.029 0.566826,0.1298 2.074998,0.8042 0.716719,0.3204 1.336374,0.5906 1.377013,0.6003 0.06298,0.015 0.163347,-0.1301 0.679776,-0.9827 0.779361,-1.2867 1.206034,-1.93069997 1.38062,-2.08399997 0.321543,-0.2823 0.794958,-0.3597 1.169948,-0.1911 0.205475,0.092 0.861685,0.7361 1.982714,1.94499997 0.592176,0.6387 0.666986,0.707 0.751926,0.6872 0.112865,-0.026 0.603398,-0.3586 1.765095,-1.1954 0.474613,-0.3418 0.981623,-0.68199997 1.126694,-0.75589997 0.248622,-0.1266 0.279187,-0.1333 0.532445,-0.1169 0.473378,0.031 0.787018,0.2275 0.982752,0.61679997 0.110998,0.2208 0.349805,0.8301 0.812583,2.0732 0.403622,1.0842 0.42052,1.1215 0.508156,1.1215 0.121064,0 0.816591,-0.1402 1.729024,-0.3484 1.723196,-0.3932 1.923894,-0.4184 2.202207,-0.2764 0.196816,0.1004 0.484804,0.3847 0.581297,0.5738 0.117231,0.2298 0.128415,0.5446 0.07943,2.2355 -0.02548,0.8795 -0.03881,1.6065 -0.02964,1.6157 0.02987,0.03 0.537095,0.127 1.551157,0.2969 1.188291,0.1992 1.962265,0.3591 2.135066,0.4411 0.25157,0.1193 0.498388,0.5065 0.549679,0.8623 0.04204,0.2915002 -0.05839,0.5787002 -0.698985,1.9986002 -0.452068,1.0021 -0.731621,1.6675 -0.709167,1.6881 0.03626,0.033 0.97116,0.6269 1.768613,1.123 1.135222,0.7062 1.289266,0.827 1.450242,1.1375 0.115105,0.2221 0.122223,0.2566 0.10955,0.5313 -0.0088,0.1917 -0.03825,0.3448 -0.08462,0.4406 -0.04382,0.091 -0.35262,0.4214 -0.805517,0.863 -1.475039,1.438 -1.820129,1.7892 -1.820129,1.8521 0,0.035 0.262461,0.4312 0.583247,0.8798 0.91841,1.2843 1.426649,2.0529 1.474457,2.2298 0.08653,0.3202 -0.0188,0.7268 -0.259198,1.0006 -0.189265,0.2156 -0.69437,0.4444 -2.349311,1.0645 -1.135942,0.4256 -1.155073,0.4348 -1.167861,0.5632 -0.01525,0.1532 0.162526,1.0177 0.490743,2.3865 l 0.274628,1.1453 -0.146442,0.3082 c -0.168753,0.3552 -0.420814,0.6211 -0.648809,0.6844 -0.155361,0.043 -1.60336,0.047 -2.988376,0.01 -0.454414,-0.013 -0.831421,-0.021 -0.837796,-0.018 -0.0064,0 -0.118273,0.6559 -0.248671,1.4506 -0.340992,2.0783 -0.376308,2.2036 -0.702115,2.4908 -0.516054,0.455 -0.771128,0.4309 -2.193556,-0.2073 -1.134567,-0.5091 -2.034693,-0.8936 -2.091767,-0.8936 -0.03014,0 -0.318475,0.4318 -0.664808,0.9956 -0.812426,1.3226 -0.834932,1.3575 -1.122717,1.7426 -0.2396,0.3206 -0.423778,0.4864 -0.623357,0.5611 -0.141358,0.053 -0.480987,0.08 -0.585136,0.046 z M 7.1237428,22.045194 c 0.1166825,-0.043 0.2848185,-0.2017 0.3533249,-0.3329 0.1047768,-0.2006 0.126283,-0.4726 0.1603503,-2.0283 0.034852,-1.5915 0.062784,-1.9146 0.1976867,-2.2862 0.1997081,-0.5502 0.8030819,-0.9238 1.427775,-0.884 0.438578,0.028 0.7510613,0.2685 0.8551053,0.6584 0.07829,0.2934 0.120962,1.1115 0.149764,2.8712 l 0.02571,1.571 0.227433,0.2128 c 0.351202,0.3285 0.700558,0.3854 1.063032,0.1729 0.220154,-0.129 0.337605,-0.3135 0.412473,-0.6481 0.116834,-0.522 0.05929,-3.9169 -0.07861,-4.6376 -0.07558,-0.395 -0.232795,-0.6685 -0.567323,-0.987 -0.457138,-0.4352 -0.848598,-0.5778 -1.5828653,-0.5765 -0.926068,0 -1.383176,0.1794 -2.0082818,0.781 l -0.1906281,0.1835 -0.022184,-0.1068 c -0.082728,-0.3982 -0.2460722,-0.6801 -0.458917,-0.7922 -0.1748558,-0.092 -0.6640174,-0.094 -0.8437561,0 -0.1653021,0.083 -0.2909737,0.3296 -0.3356605,0.6573 -0.018857,0.1383 -0.044434,1.1851 -0.056835,2.3262 -0.033665,3.0974 0.022638,3.591 0.4338801,3.8036 0.2281903,0.118 0.5889207,0.1372 0.838518,0.045 z m 10.5518952,0 c 0.675383,-0.095 1.385079,-0.4733 1.779574,-0.9474 0.227639,-0.2735 0.337236,-0.5006 0.360144,-0.7463 0.03267,-0.3504 -0.159494,-0.5792 -0.510891,-0.6084 -0.257929,-0.021 -0.363413,0.035 -0.944072,0.5027 -0.579805,0.4671 -0.786811,0.579 -1.172071,0.6335 -0.346749,0.049 -0.791661,0 -1.04778,-0.1117 -0.476044,-0.2105 -0.847752,-0.8095 -0.917815,-1.4788 l -0.02555,-0.2441 1.921158,-0.018 c 2.319052,-0.021 2.441138,-0.038 2.690013,-0.3711 0.106867,-0.1429 0.128627,-0.8912 0.03785,-1.3016 -0.222404,-1.0052 -1.0165,-1.8163 -2.096323,-2.1411 -0.325591,-0.098 -1.175349,-0.1351 -1.585908,-0.069 -1.18816,0.1904 -2.045439,0.8165 -2.464819,1.8002 -0.51064,1.1977 -0.418399,2.8085 0.214164,3.74 0.461354,0.6794 1.128385,1.1511 1.851942,1.3098 0.547781,0.1201 1.284185,0.1398 1.910384,0.051 z m -2.428395,-4.2535 c 0.01469,-0.2053 0.05097,-0.3214 0.188971,-0.6051 0.190895,-0.3924 0.334694,-0.5578 0.597414,-0.6872 0.16143,-0.079 0.225549,-0.088 0.674122,-0.088 0.480877,0 0.501615,0 0.692378,0.1089 0.435458,0.2412 0.774705,0.8317 0.775999,1.3508 l 4.41e-4,0.1744 H 16.702817 15.22907 Z m 8.646237,4.2369 c 0.322262,-0.1327 0.483505,-0.5515 1.282073,-3.3305 0.178361,-0.6207 0.335837,-1.1164 0.349945,-1.1016 0.01411,0.015 0.135768,0.3987 0.270355,0.8531 0.823269,2.7797 0.940295,3.1223 1.155944,3.3839 0.15783,0.1915 0.324165,0.2575 0.655952,0.2604 0.542086,0 0.887669,-0.2534 1.107242,-0.8267 0.497807,-1.2998 1.763983,-5.2187 1.762078,-5.4537 -0.0014,-0.1704 -0.131814,-0.4009 -0.292858,-0.5176 -0.314851,-0.228 -0.711097,-0.2294 -0.968555,0 -0.192704,0.1692 -0.340306,0.5946 -1.181465,3.405 -0.176812,0.5907 -0.333248,1.074 -0.347635,1.074 -0.02797,0 -0.06261,-0.1133 -0.622811,-2.0386 -0.581779,-1.9995 -0.731653,-2.363 -1.038826,-2.5197 -0.12541,-0.064 -0.201165,-0.073 -0.521568,-0.062 -0.345206,0.011 -0.387205,0.021 -0.53112,0.1208 -0.293981,0.2039 -0.384902,0.4381 -1.070139,2.7558 -0.321208,1.0865 -0.542375,1.7621 -0.568337,1.7361 -0.0066,-0.01 -0.235095,-0.7717 -0.507735,-1.7003 -0.84109,-2.8645 -0.85019,-2.8842 -1.346179,-2.9122 -0.355065,-0.02 -0.494576,0.015 -0.659556,0.1636 -0.173467,0.1567 -0.236022,0.33 -0.209124,0.5791 0.04908,0.4547 1.572064,5.0728 1.857351,5.632 0.109246,0.2142 0.274747,0.393 0.433326,0.4682 0.268159,0.1273 0.727762,0.1434 0.991642,0.035 z"
        />
      </SvgIcon>
    );
  }
}

interface ISCListProps {
  scList: IAdminPanelServiceCall[];
  updateServiceCalls: Function;
  isLoading: boolean;
  token?: string;
}

interface ISCListWithDataProps {
  scList: IAdminPanelServiceCall[];
  isLoading: boolean;
}

type Order = "asc" | "desc";
type ServiceCallFilter = "closed" | "open";

function desc<T>(a: T, b: T, orderBy: keyof T) {
  if (orderBy !== "DateSubmitted") {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
  } else {
    const aSC = (a as unknown) as IAdminPanelServiceCall;
    const bSC = (b as unknown) as IAdminPanelServiceCall;
    var aDate: Date = new Date(aSC.dateSubmitted);
    var bDate: Date = new Date(bSC.dateSubmitted);

    if (bDate < aDate) {
      return -1;
    }
    if (bDate > aDate) {
      return 1;
    }
  }
  return 0;
}

function getSorting<K extends keyof any>(order: Order, orderBy: K): (a: { [key in K]: number | string }, b: { [key in K]: number | string }) => number {
  return order === "desc" ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

function sortTable<T>(array: T[], compare: (x: T, y: T) => number) {
  const rows = array.map((item, index) => [item, index] as [T, number]);

  rows.sort((x, y) => {
    const order = compare(x[0], y[0]);
    if (order !== 0) {
      return order;
    }

    return x[1] - y[1];
  });

  return rows.map(item => item[0]);
}

function ServiceCallList(props: ISCListProps) {
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof IAdminPanelServiceCall>("id");
  const [filtered, setFiltered] = React.useState<ServiceCallFilter>("open");
  const [open, setOpen] = React.useState(false);
  const [openRowIndex, setOpenRowIndex] = React.useState("0");

  const handleClick = (id: string) => {
    if (openRowIndex.toString() !== id) {
      if (!open) {
        setOpen(!open);
      }
      setOpenRowIndex(id);
    } else {
      setOpen(!open);
    }
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof IAdminPanelServiceCall) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  async function handleChangeServiceCallFilter(event: React.ChangeEvent<HTMLInputElement>) {
    if (filtered === "open") {
      setFiltered("closed");
      props.updateServiceCalls(true);
    } else {
      setFiltered("open");
      props.updateServiceCalls(false);
    }
  }

  return (
    <div>
      <FormatTableToolbar />
      <SettingsGrid>
        <Grid component="label" container alignItems="center" spacing={1}>
          <Grid item>Closed</Grid>
          <Grid item>
            <Switch checked={filtered === "open"} onChange={handleChangeServiceCallFilter} color="default" />
          </Grid>
          <Grid item>Open</Grid>
        </Grid>
        <Grid component="label" container alignItems="center" spacing={1}>
          <Grid item></Grid>
        </Grid>
      </SettingsGrid>
      <WithLoadingSpinners message="Gathering data" isLoading={props.isLoading}>
        <Table aria-labelledby="tableTitle" size="medium" aria-label="sc-table">
          <FormatTableHead order={order} orderBy={orderBy} rowCount={props.scList.length} onRequestSort={handleRequestSort} />
          <TableBody>
            {props.token &&
              sortTable(props.scList, getSorting(order, orderBy)).map(sc => (
                <>
                  <TableRow key={sc.id} hover>
                    <TableCell>{GetNewImage(sc.dateSubmitted)}</TableCell>
                    <TableCell>{sc.id}</TableCell>
                    <TableCell>{sc.internalStatus}</TableCell>
                    <TableCell>{sc.companyName}</TableCell>
                    <TableCell>
                      {sc.firstName} {sc.lastName}
                    </TableCell>
                    <TableCell>{moment(sc.dateSubmitted).fromNow()}</TableCell>
                    <TableCell>
                      <CompleteSignUpButton call={sc} />
                    </TableCell>
                    <TableCell>
                      {open && openRowIndex.toString() === sc.id ? (
                        <ExpandLessIcon onClick={() => handleClick(sc.id)} />
                      ) : (
                        <ExpandMoreIcon onClick={() => handleClick(sc.id)} />
                      )}
                    </TableCell>
                  </TableRow>
                  <HiddenTableRow open={open && openRowIndex.toString() === sc.id}>
                    <StyledTableCell />
                    <StyledTableCell colSpan={7}>
                      <TableRow>
                        <StyledTableCell>
                          <strong>Product:</strong>
                        </StyledTableCell>
                        <StyledTableCell colSpan={4}>{sc.product}</StyledTableCell>
                      </TableRow>
                      <TableRow>
                        <StyledTableCell>
                          <strong>Type:</strong>
                        </StyledTableCell>
                        <StyledTableCell colSpan={4}>{sc.productType}</StyledTableCell>
                      </TableRow>
                      <TableRow>
                        <StyledTableCell>
                          <strong>Area:</strong>
                        </StyledTableCell>
                        <StyledTableCell colSpan={4}>{getArea(sc)}</StyledTableCell>
                      </TableRow>
                      <TableRow>
                        <StyledTableCell>
                          <strong>Description:</strong>
                        </StyledTableCell>
                        <StyledTableCell>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: formatDescription(sc.description),
                            }}
                          />
                        </StyledTableCell>
                      </TableRow>
                    </StyledTableCell>
                  </HiddenTableRow>
                </>
              ))}
          </TableBody>
        </Table>
      </WithLoadingSpinners>
    </div>
  );
}

interface ISignUpServiceCallListWithDataProps {
  token?: string;
}

class SignUpServiceCallListWithData extends React.Component<ISignUpServiceCallListWithDataProps, ISCListWithDataProps> {
  constructor(props: ISignUpServiceCallListWithDataProps) {
    super(props);

    this.state = {
      scList: [],
      isLoading: false,
    };

    this.getServiceCalls = this.getServiceCalls.bind(this);
    this.updateServiceCallList = this.updateServiceCallList.bind(this);
  }

  services: Services = new Services();
  mounted: boolean = false;

  async getServiceCalls(getClosed: boolean) {
    this.setState({ isLoading: true });
    try {
      if (!this.props.token) {
        throw new Error("User unauthorized");
      }

      const signupRepo = await this.services.SignUpRepository();
      const result = await signupRepo.GetSignUpServiceCalls(this.props.token, getClosed);

      if (this.mounted) {
        this.setState({ scList: result, isLoading: false });
      }
    } catch (reason) {
      snackbarService.error(reason);
      this.setState({ isLoading: false });
    }
  }

  updateServiceCallList(getClosed: boolean) {
    this.getServiceCalls(getClosed);
  }

  componentDidMount() {
    this.mounted = true;
    this.getServiceCalls(false);
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  render() {
    return (
      <Container>
        <ServiceCallList isLoading={this.state.isLoading} scList={this.state.scList} updateServiceCalls={this.updateServiceCallList} token={this.props.token} />
      </Container>
    );
  }
}

export default connect((state: IApplicationState) => {
  return selectAuth(state);
})(SignUpServiceCallListWithData);
